import React from 'react';
import pageStyles from '../WarrantyRequestHistoryPage.module.scss';
import clsx from 'clsx';
import Chevron from 'assets/icons/chevron.svg?react';
import Accepted from '../../../../assets/icons/status_accepted.svg';
import Failed from '../../../../assets/icons/status_failed.svg';
import Warning from '../../../../assets/icons/status_warning.svg';
import Pending from '../../../../assets/icons/status_pending.svg';
import { Tip } from '../../../../components/uikit/Tip.tsx';
import i18n from 'i18next';
import { formatDate } from '../../../../helpers/abas-helper.ts';
import { WarrantyRequestOrder } from '../../../../services/api/api-client.types.ts';
import { InnerAccordion } from './InnerAccordion.tsx';

type OwnProps = {
  data: WarrantyRequestOrder[];
  openedOrders: Set<string>;
  handleClickAccordion: (key: string) => void;
};

const RejectedOrderStatusTooltip = () => {
  return (
    <div className={clsx(pageStyles.tooltipText)}>
      <div>{i18n.t('Page.WarrantyHistoryPage.rejected')}</div>
      <div className={pageStyles.disabledOption}>
        {i18n.t('Page.WarrantyHistoryPage.rejected_order')}
      </div>
    </div>
  );
};

const WarningStatusTooltip = () => {
  return (
    <div className={clsx(pageStyles.tooltipText)}>
      <div>{i18n.t('Page.WarrantyHistoryPage.partly_accepted')}</div>
      <div className={pageStyles.disabledOption}>
        {i18n.t('Page.WarrantyHistoryPage.partly_accepted_order')}
      </div>
    </div>
  );
};

const StatusAccepted = 'icon:ok';
export const StatusFailed = 'icon:stop';
const StatusPending = 'icon:hourglass';

export const getStatusImage = (garanties: string[], isItem?: boolean) => {
  let iconStatus = null;
  let wasAcceptedMet = false;
  let wasFailedMet = false;

  const startOfTestId = isItem ? 'item' : 'group';

  for (const garantie of garanties) {
    if (garantie === StatusPending || garantie === '' || garantie === null) {
      return (
        <Tip
          testId={`${startOfTestId}-pending-tip`}
          className={pageStyles.unsetBackgroundColor}
          tooltipText={i18n.t('Page.WarrantyHistoryPage.being_processed')}
          customImage={Pending}
        />
      );
    }

    if (garantie === StatusAccepted) {
      wasAcceptedMet = true;
      iconStatus = Accepted;
    }

    if (garantie === StatusFailed) {
      wasFailedMet = true;
      iconStatus = Failed;
    }

    if (wasFailedMet && wasAcceptedMet) {
      return (
        <Tip
          testId={`${startOfTestId}-warning-tip`}
          className={pageStyles.unsetBackgroundColor}
          tooltipBody={<WarningStatusTooltip />}
          customImage={Warning}
        />
      );
    }
  }

  if (iconStatus === Accepted) {
    return (
      <Tip
        testId={`${startOfTestId}-accepted-tip`}
        className={pageStyles.unsetBackgroundColor}
        tooltipText={i18n.t('Page.WarrantyHistoryPage.accepted')}
        customImage={Accepted}
      />
    );
  } else if (iconStatus === Failed) {
    return (
      <Tip
        testId={`${startOfTestId}-failed-tip`}
        className={pageStyles.unsetBackgroundColor}
        tooltipClassName={pageStyles.description}
        tooltipBody={<RejectedOrderStatusTooltip />}
        customImage={Failed}
      />
    );
  } else {
    return null;
  }
};

export const Accordion = (props: OwnProps) => {
  const { data, openedOrders, handleClickAccordion } = props;

  const outerTableTextStyles = [pageStyles.text, pageStyles.overflowEllipsis];

  return (
    <div className={pageStyles.marginBottom65}>
      {data.map((item) => (
        <div data-test-id={'group-container'} key={item.orderNumber}>
          <div
            className={clsx(
              pageStyles.rowContainer,
              !openedOrders.has(item.orderNumber) && pageStyles.borderBottom,
            )}
          >
            <Chevron
              data-test-id={'group-expand'}
              className={clsx(
                pageStyles.pointer,
                !openedOrders.has(item.orderNumber) && pageStyles.transform180,
              )}
              onClick={() => handleClickAccordion(item.orderNumber)}
            />
            <div
              data-test-id={'group-order-number'}
              className={clsx(outerTableTextStyles, pageStyles.flex1)}
            >
              {item.orderNumber}
            </div>
            <div
              data-test-id={'group-date'}
              className={clsx(outerTableTextStyles, pageStyles.flex1)}
            >
              {formatDate(item.from ?? '')}
            </div>
            <div
              data-test-id={'group-reference'}
              className={clsx(outerTableTextStyles, pageStyles.flex1)}
            >
              {item.reference}
            </div>
            <div className={clsx(outerTableTextStyles, pageStyles.flex1)}>
              {item.order}
            </div>
            <div
              data-test-id={'group-count'}
              className={clsx(outerTableTextStyles, pageStyles.width117)}
            >
              {item.requests.flatMap((order) => order.parts).length}
            </div>
            <div className={clsx(outerTableTextStyles, pageStyles.width70)}>
              {getStatusImage(
                item.requests
                  .flatMap((order) => order.parts)
                  .map((part) => part.garantie),
              )}
            </div>
          </div>
          {openedOrders.has(item.orderNumber) && (
            <div className={pageStyles.paddingLeft35}>
              <InnerAccordion data={item.requests} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
